<template>
  <Dialog
    :visible="visible"
    modal
    :header="dialogHeader"
    class="w-full max-w-3xl"
    append-to="body"
    data-testid="pm-project-viewer-dialog"
    @update:visible="onVisibleChange"
  >
    <div v-if="project" class="flex flex-col gap-6">
      <div class="flex items-center gap-3">
        <h3 class="text-lg font-semibold text-[var(--color-dark)] m-0">
          {{ project.name }}
        </h3>
        <Tag
          :value="statusLabel"
          :severity="statusSeverity"
        />
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium uppercase tracking-wide text-[var(--color-disabled)]">
            {{ $t('ProjectManagement.viewer.description') }}
          </span>
          <span class="text-sm text-[var(--color-dark)]">
            {{ project.description || '—' }}
          </span>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium uppercase tracking-wide text-[var(--color-disabled)]">
            {{ $t('ProjectManagement.viewer.dates') }}
          </span>
          <span class="text-sm text-[var(--color-dark)]">
            {{ formatDate(project.startDate) }} – {{ formatDate(project.endDate) }}
          </span>
        </div>
        <div class="flex flex-col gap-1 sm:col-span-2">
          <span class="text-xs font-medium uppercase tracking-wide text-[var(--color-disabled)]">
            {{ $t('ProjectManagement.viewer.lead') }}
          </span>
          <div class="flex items-center gap-2">
            <div
              class="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden shrink-0 border border-slate-200"
            >
              <img
                v-if="leadUser?.picture_profile?.small"
                :src="leadUser.picture_profile.small"
                class="w-8 h-8 object-cover"
                alt=""
              />
              <img
                v-else
                :src="defaultProfileImage"
                class="w-8 h-8 object-cover"
                alt=""
              />
            </div>
            <span class="text-sm text-[var(--color-dark)]">
              {{ leadDisplayName }}
            </span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <h4 class="text-sm font-semibold text-[var(--color-dark)] m-0">
          {{ $t('ProjectManagement.viewer.teams') }}
        </h4>
        <div
          v-if="viewerTeams.length === 0"
          class="text-sm text-[var(--color-disabled)]"
        >
          {{ $t('ProjectManagement.viewer.teamsEmpty') }}
        </div>
        <div v-else class="flex flex-wrap gap-2">
          <button
            v-for="team in viewerTeams"
            :key="team._id"
            type="button"
            class="rounded-lg border px-3 py-2 text-left transition-colors cursor-pointer bg-white"
            :class="selectedTeamId === String(team._id)
              ? 'border-[var(--color-orange)] bg-orange-50'
              : 'border-slate-200 hover:border-slate-300'"
            data-testid="pm-viewer-team"
            @click="toggleTeam(String(team._id))"
          >
            <div class="text-sm font-medium text-[var(--color-dark)]">
              {{ team.name }}
            </div>
            <div class="text-xs text-[var(--color-disabled)]">
              {{ $t('ProjectManagement.viewer.memberCount', { count: memberCount(team) }) }}
            </div>
          </button>
        </div>
      </div>

      <div v-if="selectedTeam" class="flex flex-col gap-3">
        <h4 class="text-sm font-semibold text-[var(--color-dark)] m-0">
          {{ $t('ProjectManagement.viewer.members') }} — {{ selectedTeam.name }}
        </h4>
        <div
          v-if="selectedTeamMembers.length === 0"
          class="text-sm text-[var(--color-disabled)]"
        >
          {{ $t('ProjectManagement.viewer.membersEmpty') }}
        </div>
        <div
          v-else
          class="flex flex-col gap-2 max-h-64 overflow-y-auto"
          data-testid="pm-viewer-members"
        >
          <div
            v-for="member in selectedTeamMembers"
            :key="member.id"
            class="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3"
          >
            <div
              class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden shrink-0 border border-slate-200"
            >
              <img
                v-if="member.picture_profile?.small"
                :src="member.picture_profile.small"
                class="w-10 h-10 object-cover"
                alt=""
              />
              <img
                v-else
                :src="defaultProfileImage"
                class="w-10 h-10 object-cover"
                alt=""
              />
            </div>
            <div class="min-w-0 flex-1 space-y-1">
              <div class="text-sm font-medium text-[var(--color-dark)]">
                {{ member.name || $t('ProjectManagement.viewer.unknownUser') }}
              </div>
              <div class="text-sm text-[var(--color-disabled)]">
                {{ member.email || '—' }}
              </div>
              <div class="text-xs font-mono text-[var(--color-disabled)]">
                {{ member.id }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else-if="viewerTeams.length > 0"
        class="text-sm text-[var(--color-disabled)]"
      >
        {{ $t('ProjectManagement.viewer.selectTeam') }}
      </div>
    </div>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import Tag from 'primevue/tag';
import defaultProfileImage from '@/assets/defaultProfileImage';
import { getUserDisplayName } from '@/utils/userParticipants';

export default {
  name: 'ProjectViewerDialog',
  components: {
    Dialog,
    Tag,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    project: {
      type: Object,
      default: null,
    },
    teams: {
      type: Array,
      default: () => [],
    },
    userLookup: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['update:visible'],
  data() {
    return {
      selectedTeamId: null,
      defaultProfileImage,
    };
  },
  computed: {
    dialogHeader() {
      return this.$t('ProjectManagement.viewer.title');
    },
    viewerTeams() {
      const teamIds = Array.isArray(this.project?.teams)
        ? this.project.teams.map(String)
        : [];
      if (teamIds.length === 0) return [];
      const idSet = new Set(teamIds);
      return (this.teams || []).filter((team) => idSet.has(String(team._id)));
    },
    selectedTeam() {
      if (!this.selectedTeamId) return null;
      return this.viewerTeams.find((team) => String(team._id) === this.selectedTeamId) || null;
    },
    selectedTeamMembers() {
      if (!this.selectedTeam) return [];
      return this.getTeamRosterIds(this.selectedTeam).map((id) => {
        const user = this.userLookup[id];
        return {
          id,
          name: user ? getUserDisplayName(user) : '',
          email: user?.email || '',
          picture_profile: user?.picture_profile || null,
        };
      });
    },
    leadUser() {
      const leadId = this.project?.projectLeadUserId;
      if (!leadId) return null;
      return this.userLookup[String(leadId)] || null;
    },
    leadDisplayName() {
      if (!this.project?.projectLeadUserId) return '—';
      return getUserDisplayName(this.leadUser) || this.project.projectLeadUserId;
    },
    statusLabel() {
      const status = this.project?.status || 'active';
      return this.$t(`ProjectManagement.status.${status}`);
    },
    statusSeverity() {
      const status = this.project?.status || 'active';
      if (status === 'completed') return 'success';
      if (status === 'archived') return 'secondary';
      return 'info';
    },
  },
  watch: {
    visible(isVisible) {
      if (!isVisible) {
        this.selectedTeamId = null;
      }
    },
    project() {
      this.selectedTeamId = null;
    },
  },
  methods: {
    onVisibleChange(value) {
      this.$emit('update:visible', value);
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString();
    },
    memberCount(team) {
      return this.getTeamRosterIds(team).length;
    },
    getTeamRosterIds(team) {
      const members = Array.isArray(team?.members) ? team.members.map(String) : [];
      const leaders = Array.isArray(team?.leaders) ? team.leaders.map(String) : [];
      return Array.from(new Set([...leaders, ...members]));
    },
    toggleTeam(teamId) {
      this.selectedTeamId = this.selectedTeamId === teamId ? null : teamId;
    },
  },
};
</script>
